import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";

import { PaymentsSubscriptionUpdateAttributes } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import {
  useCardDialog,
  useCardWaitReport,
} from "~/components/autopto/cardDialog/useCardDialog";
import { isAutoptoTrialing } from "~/components/autopto/isAutoptoTrial";
import WithSubscription from "~/components/subscription/WithSubscription";
import BlockedAccessNotice from "~/components/subscription/blockedAccess/BlockedAccessNotice";
import PausedAccessNotice from "~/components/subscription/blockedAccess/PausedAccessNotice";
import { useBlockedAccessRecovery } from "~/components/subscription/blockedAccess/useBlockedAccessRecovery";
import { useSavedCardRecheck } from "~/components/subscription/useSavedCardRecheck";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { useBillingAccess } from "~/contexts/BillingAccessContext";
import { useTeam } from "~/contexts/TeamProvider";
import { messageFromError } from "~/helpers/messageFromError";
import { tryCatch } from "~/helpers/tryCatch";
import { usePageTitle } from "~/hooks/usePageTitle";
import { useStagedBoolean } from "~/hooks/useStagedBoolean";
import { routes } from "~/router";

import { Subscription } from "../../api/types";
import { Icon } from "../../assets/icons/Icon";
import NoData from "../../components/containers/NoData";
import styles from "./Billing.module.scss";

export const getSubscriptionANDPlanUsageData = async () => {
  // Independent requests: one after the other, the page waited for both.
  const [subscriptionData, planUsageData] = await Promise.all([
    apiClient.billing.subscriptionList({
      headers: {
        "Authorize-Member-Actions": "create, update, cancel, restore, checkout",
      },
    }),
    apiClient.teams.usageList(),
  ]);

  return {
    planUsageData: planUsageData?.data,
    subscriptionData: subscriptionData?.data,
    // @ts-expect-error BE types
    subscriptionAuthorizations: subscriptionData?.data?.authorizations,
  };
};

interface SubscriptionEntry {
  key: string;
  divide_by: number;
  amount: number;
  price_id: number;
  title: string;
  quantity?: number;
  id: number;
  singleton: boolean;
}

export interface MappedData {
  priceId: number | undefined;
  tier: string | undefined;
  entries: SubscriptionEntry[];
  id: number | undefined;
}

interface TeamUsageData {
  team_usage: TeamUsage;
}

interface SubscriptionListData {
  "payments/subscription": Subscription;
}

export interface SubscriptionData {
  planUsageData: TeamUsageData;
  subscriptionData: SubscriptionListData;
}

export interface TeamUsage {
  [key: string]: {
    current_usage: number;
    plan_limits: {
      [key: string]: number;
    };
  };
}

const SubscriptionPage = () => {
  usePageTitle(PAGE_TITLES.currentPlan);
  const [openPlanModal, setOpenPlanModal] = useState(false);
  const { team, refetchTeam, isLoading: isLoadingTeam } = useTeam();
  const { isBillingDenied } = useBillingAccess();
  const queryClient = useQueryClient();
  const { blockedReason, isPolling, startPolling } = useBlockedAccessRecovery();

  // The app's card dialog: Stripe comes back here, as for any card.
  // Back from Stripe, the card dialog watches the saved card, a blocked
  // team's too (see CardSetupReturn).
  const { openCardDialog, isOpeningCardDialog, isAwaitingCard } =
    useCardDialog();

  const { data } = useQuery({
    queryKey: ["subscriptionPlans", team?.id],
    queryFn: async () => {
      const subscription = await getSubscriptionANDPlanUsageData();

      return {
        subscription,
        subscriptionAuthorizations: subscription.subscriptionAuthorizations,
      };
    },
    enabled: !!team?.subscription,
  });

  const isSubscriptionUpdateAuthorized =
    data?.subscriptionAuthorizations?.member?.includes("update");

  const { data: addons } = useQuery({
    // Its own key: the invoices page reads the same products differently.
    // The catalog depends on the team.
    queryKey: ["subscriptionAddons", team?.id],
    queryFn: async () => {
      const result = await tryCatch(
        apiClient.billing.productsList({
          "page[size]": 10,
          "page[number]": 1,
          s: "created_at ASC",
          q: {
            kind_eq: "add-on",
          },
        }),
      );

      // The backend closes the products to some teams (e.g. during an
      // Autopto trial): null hides the add-ons instead of failing.
      if (result.error?.status === 403) return null;
      if (result.error) throw result.error;

      return result.data.data?.["payments/products"];
    },
  });

  // Until the catalog answers, expect it closed where the backend closes
  // it (an Autopto trial, a card or not, and a blocked subscription) or
  // while the team is unknown, so the add-on cards don't flash.
  const isAddonsAvailable =
    addons === undefined
      ? Boolean(team) &&
        !isAutoptoTrialing(team) &&
        !team?.subscription?.blocked_reason
      : addons !== null;

  const subscriptionData =
    data?.subscription?.subscriptionData?.["payments/subscription"];

  // Undefined until the subscription loads.
  const shownPaymentMethod = data
    ? (subscriptionData?.payment_method ?? null)
    : undefined;

  const savedCardStatus = useSavedCardRecheck(shownPaymentMethod);

  useCardWaitReport("billing", savedCardStatus === "saving");

  const { mutate: updateSubscription, isPending: isPendingUpdateSubscription } =
    useMutation({
      mutationFn: (data: PaymentsSubscriptionUpdateAttributes) => {
        return apiClient.billing.subscriptionPartialUpdate(data);
      },
      onSuccess: async () => {
        enqueueSnackbar(`Subscription was successfully updated!`, {
          variant: "success",
        });
        await refetchTeam();
        await queryClient.invalidateQueries({
          queryKey: ["subscriptionPlans"],
        });

        setOpenPlanModal(false);
      },
      onError: (error) => {
        enqueueSnackbar(
          `Error updating subscription: ${messageFromError(error)}`,
          {
            variant: "error",
            persist: true,
          },
        );
      },
    });

  const { subscription } = data || {};

  const defaultAddons = addons?.filter(
    (item: { default: boolean }) => !item.default,
  );
  const entries = defaultAddons?.flatMap((item) => {
    return item.prices
      ? item.prices
          .filter((price) => price.tier === subscriptionData?.price?.tier)
          .filter((price) => !price.metered)
          .map((price) => {
            const subscriptionAddon =
              subscriptionData?.subscription_addons?.find(
                (addon) => addon.price?.product?.key === item.key,
              );

            return {
              key: item.key ?? "",
              divide_by: price.divide_by || 0,
              amount: price.amount,
              price_id: price.id,
              title: item.name,
              quantity: subscriptionAddon?.quantity,
              id: price.product_id,
              singleton: item.singleton,
            } satisfies SubscriptionEntry;
          })
      : [];
  });

  const editData: MappedData = {
    priceId: subscriptionData?.price?.id,
    tier: subscriptionData?.price?.tier,
    entries: entries || [],
    id: subscriptionData?.id,
  };

  // Show the plan card as skeletons while the team and the subscription
  // are loading — an empty section (or a bare spinner) reads as "no data".
  // Keyed off "subscription content is not here yet" rather than isLoading,
  // so refetches after an error/stale cache entry also show skeletons.
  const showSkeletons = useStagedBoolean(
    isLoadingTeam || (!!team?.subscription && !subscriptionData),
    0,
    400,
  );

  // Billing endpoints are admin-only. A member of a team that can use the
  // app goes home (the menu hides Billing from them too); otherwise the
  // guard sends everyone here, so going home would loop: show a notice.
  if (isBillingDenied && !blockedReason && team?.subscription?.healthy) {
    return <Navigate to={routes.home()} replace />;
  }

  const showBillingDetails = !isBillingDenied;

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{PAGE_TITLES.currentPlan}</h1>
      {blockedReason && (
        <BlockedAccessNotice
          blockedReason={blockedReason}
          errorMessage={team?.subscription?.error_message ?? null}
          openInvoiceId={team?.subscription?.open_invoice_id ?? null}
          isBillingAuthorized={!isBillingDenied}
          isPolling={isPolling || isAwaitingCard}
          isOpeningCardForm={isOpeningCardDialog}
          isAwaitingCard={isAwaitingCard}
          onAddCard={openCardDialog}
          onInvoiceOpened={startPolling}
        />
      )}
      {isBillingDenied && !blockedReason && <PausedAccessNotice />}
      {showBillingDetails &&
        (team?.subscription || showSkeletons ? (
          <WithSubscription
            //@ts-expect-error refactor password @TODO: fix types issue
            data={subscription}
            strapiSetup={openCardDialog}
            updateSubscription={updateSubscription}
            isPendingUpdateSubscription={isPendingUpdateSubscription}
            editSubscriptionData={editData}
            openPlanModal={openPlanModal}
            setOpenPlanModal={setOpenPlanModal}
            isFetching={showSkeletons}
            isAddonsAvailable={isAddonsAvailable}
            isCreatingSession={isOpeningCardDialog}
            isAwaitingCard={isAwaitingCard}
            savedCardStatus={savedCardStatus}
            isSubscriptionUpdate={isSubscriptionUpdateAuthorized}
            subscriptionAuthorizations={data?.subscriptionAuthorizations}
          />
        ) : (
          <NoData title="No Subscription">
            <Link
              to={routes.billing.subscriptionEdit()}
              className={styles.linkButton}
              onClick={() => localStorage.removeItem("siteId")}
            >
              {/*
             // @ts-expect-error TS2339 non-existing className */}
              <span className={styles.emptyDashboardButton}>
                <Icon iconName="PlusIcon" /> Browse Plans
              </span>
            </Link>
          </NoData>
        ))}
    </div>
  );
};

export default SubscriptionPage;
